import { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { AppState } from "@Store/index";
import {
  setSmartFilterOpen,
  setSmartFilterQuery,
  submitSmartSearch,
  clearSmartFilters,
  clearSmartFilterResults,
  removeFilterAndUpdateCount,
  removeExtractedButNotApplied,
  mapNlpResponseToFilterState,
} from "./smartFilterReducer";
import { applyFilter } from "@Pages/Search/data/searchPageReducer";
import { SmartSearchRequest } from "./types";
import { COUNTRIES_WITH_MONTHLY_DURATION } from "constants/configConstants";

const MAX_QUERY_LENGTH = 500;

/**
 * Sanitizes user input before sending to the NLP API.
 * - Enforces max length
 * - Strips HTML/template injection characters
 * - Removes control characters
 */
const sanitizeQuery = (input: string): string =>
  input
    .slice(0, MAX_QUERY_LENGTH)
    .replace(/[<>{}]/g, "")
    .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, "") // eslint-disable-line no-control-regex
    .trim();

/**
 * Hook that manages the Smart NLP Filter lifecycle:
 * - Opening/closing the panel
 * - Submitting the natural language query
 * - Mapping NLP response to search filters
 * - Applying filters via existing applyFilter thunk
 * - Fetching inventory count (mweb)
 */
const useSmartFilter = () => {
  const dispatch = useDispatch();
  const abortControllerRef = useRef<AbortController | null>(null);

  const {
    query,
    isLoading,
    isCountLoading,
    isOpen,
    appliedFilters,
    extractedButNotApplied,
    isIrrelevant,
    message,
    error,
    inventoryCount,
  } = useSelector(
    ({ smartFilterData }: AppState) => ({
      query: smartFilterData?.query || "",
      isLoading: smartFilterData?.isLoading || false,
      isCountLoading: smartFilterData?.isCountLoading || false,
      isOpen: smartFilterData?.isOpen || false,
      appliedFilters: smartFilterData?.appliedFilters || null,
      extractedButNotApplied: smartFilterData?.extractedButNotApplied || null,
      isIrrelevant: smartFilterData?.isIrrelevant || false,
      message: smartFilterData?.message || null,
      error: smartFilterData?.error || null,
      inventoryCount: smartFilterData?.inventoryCount ?? null,
    }),
    shallowEqual,
  );

  const { region, canonical_name, dynamicFilterCountData, currentFilters } = useSelector(
    ({ searchPageData }: AppState) => ({
      region: searchPageData.region,
      canonical_name: searchPageData.canonical_name,
      dynamicFilterCountData: searchPageData.dynamicFilterCountData,
      currentFilters: searchPageData.filters,
    }),
    shallowEqual,
  );

  // Cleanup abort controller on unmount
  // eslint-disable-next-line arrow-body-style
  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  const togglePanel = useCallback(() => {
    dispatch(setSmartFilterOpen(!isOpen));
  }, [dispatch, isOpen]);

  const openPanel = useCallback(() => {
    dispatch(setSmartFilterOpen(true));
  }, [dispatch]);

  const closePanel = useCallback(() => {
    dispatch(setSmartFilterOpen(false));
  }, [dispatch]);

  const updateQuery = useCallback(
    (text: string) => {
      dispatch(setSmartFilterQuery(text));
    },
    [dispatch],
  );

  /**
   * Submits the smart search query to the NLP API.
   * Constructs the request payload from the current search region context.
   */
  const submitQuery = useCallback(() => {
    const sanitizedText = sanitizeQuery(query);
    if (!sanitizedText || !region || !canonical_name) return;

    // Abort any in-flight request
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    // Determine lease_duration_type from region country
    const countryShortName =
      region?.location?.country?.short_name ||
      region?.ancestors_short?.find((a: any) => a.region_type === "country")?.short_name ||
      "";
    const leaseDurationType: "weekly" | "monthly" = COUNTRIES_WITH_MONTHLY_DURATION.includes(
      countryShortName,
    )
      ? "monthly"
      : "weekly";

    // Extract city name from region ancestors
    const cityAncestor = region?.ancestors_short?.find(
      (a: any) => a.region_type === "locality" || a.region_type === "sublocality",
    );
    const cityName = region?.name || cityAncestor?.name || "";

    // Collect top 10 available amenities from filter count data (amenities_grouped)
    const amenitiesGrouped = dynamicFilterCountData?.data?.data?.amenities_grouped || [];
    const availableAmenities: string[] = amenitiesGrouped
      .flatMap((category: any) =>
        (category.values || []).map((a: any) => ({ name: a.name, count: a.count })),
      )
      .sort((a: any, b: any) => b.count - a.count)
      .slice(0, 10)
      .map((a: any) => a.name);

    const request: SmartSearchRequest = {
      text: sanitizedText,
      canonical_name,
      lease_duration_type: leaseDurationType,
      city: cityName,
      parent_id: ["establishment", "sublocality"].includes(region?.region_type)
        ? region?.parent_id
        : region?.id,
      ...(availableAmenities.length > 0 && { available_amenities: availableAmenities }),
      ...(region?.region_inventory_price_range && {
        region_inventory_price_range: {
          ...region.region_inventory_price_range,
          min_price: dynamicFilterCountData?.data?.data?.min_price,
          max_price: dynamicFilterCountData?.data?.data?.max_price,
          min_available_price: dynamicFilterCountData?.data?.data?.min_available_price,
          max_available_price: dynamicFilterCountData?.data?.data?.max_available_price,
          min_weekly_price: dynamicFilterCountData?.data?.data?.min_weekly_price,
          max_weekly_price: dynamicFilterCountData?.data?.data?.max_weekly_price,
          min_monthly_price: dynamicFilterCountData?.data?.data?.min_monthly_price,
          max_monthly_price: dynamicFilterCountData?.data?.data?.max_monthly_price,
        },
      }),
      ...(region?.region_type === "sublocality"
        ? { locality_canonical_name: region?.canonical_name }
        : {}),
    };

    dispatch(submitSmartSearch(request, controller.signal));
  }, [query, region, canonical_name, dynamicFilterCountData, dispatch]);

  /**
   * Applies the extracted NLP filters to the search page.
   * Uses the existing `applyFilter` thunk which handles URL update + data fetch.
   */
  const applySmartFilters = useCallback(() => {
    if (!appliedFilters) return;

    const isMonthlyCountry = COUNTRIES_WITH_MONTHLY_DURATION.includes(
      region?.location?.country?.short_name || "",
    );
    const regionMinPrice = isMonthlyCountry
      ? region?.region_inventory_price_range?.min_available_price
      : region?.region_inventory_price_range?.min_weekly_price;
    const regionMaxPrice = isMonthlyCountry
      ? region?.region_inventory_price_range?.max_available_price
      : region?.region_inventory_price_range?.max_weekly_price;

    const nlpFilterState = mapNlpResponseToFilterState(
      appliedFilters,
      regionMinPrice || 0,
      regionMaxPrice || 0,
      isMonthlyCountry,
    );

    // Merge NLP filters with existing filters — NLP values override existing ones
    const mergedFilters = { ...currentFilters, ...nlpFilterState };

    dispatch(applyFilter(mergedFilters));
    closePanel();
  }, [appliedFilters, region, currentFilters, dispatch, closePanel]);

  const clearAll = useCallback(() => {
    abortControllerRef.current?.abort();
    dispatch(clearSmartFilters());
  }, [dispatch]);

  const clearResults = useCallback(() => {
    abortControllerRef.current?.abort();
    dispatch(clearSmartFilterResults());
  }, [dispatch]);

  const removeFilter = useCallback(
    (filterKey: string) => {
      dispatch(removeFilterAndUpdateCount(filterKey));
    },
    [dispatch],
  );

  const removeUnappliedFilter = useCallback(
    (index: number) => {
      dispatch(removeExtractedButNotApplied(index));
    },
    [dispatch],
  );

  return {
    // State
    query,
    isLoading,
    isCountLoading,
    isOpen,
    appliedFilters,
    extractedButNotApplied,
    isIrrelevant,
    message,
    error,
    inventoryCount,
    // Actions
    togglePanel,
    openPanel,
    closePanel,
    updateQuery,
    submitQuery,
    applySmartFilters,
    clearAll,
    clearResults,
    removeFilter,
    removeUnappliedFilter,
  };
};

export default useSmartFilter;
